/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1785110400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "singular",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Authenticating with an API key",
          "depth": 1,
          "id": "authenticating-with-an-api-key"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h3: "h3",
      code: "code",
      img: "img",
      a: "a",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "This integration lets you send server-to-server events to Singular without writing a custom pipeline. Whether you're calculating cross-device attribution, analyzing ROI, or using Singular for fraud prevention, including event data helps you have a more complete analytics view."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Hightouch supports syncing in-app events to Singular"
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "Start by creating an instance of Singular destination in Hightouch. Upon creating your instance, you must enter a Singular API key for authentication."
      }), "\n", _jsx(_components.h3, {
        id: "authenticating-with-an-api-key",
        children: "Authenticating with an API key"
      }), "\n", _jsxs(_components.p, {
        children: ["To get your API key, navigate to ", _jsx(_components.code, {
          children: "Developer Tools"
        }), " > ", _jsx(_components.code, {
          children: "API Keys"
        }), " and copy your API key into Hightouch.\n", _jsx(_components.img, {
          src: "destinations/destination-singular-api-key.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: ["Any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "your source"
        }), " are treated as new events and are sent to Singular when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "select-api-version",
        children: "Select API version"
      }), "\n", _jsx(_components.p, {
        children: "Singular's EVENT endpoint has two versions. V2 identifies devices with the Singular Device ID (SDID). V1 uses legacy device identifiers such as IDFA, IDFV, AIFA, and ASID."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Singular accounts created on or after July 15, 2026 must use V2. Singular\nrejects V1 requests from these accounts."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For standard event syncs, choose the version under ", _jsx(_components.strong, {
          children: "Which Singular EVENT API version should this sync use?"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Version"
            }), _jsx(_components.th, {
              children: "Use when"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "V2 (SDID)"
              })
            }), _jsx(_components.td, {
              children: "Your Singular account was created on or after July 15, 2026, or your account has SDID enabled."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "V1"
              })
            }), _jsx(_components.td, {
              children: "Your account predates July 15, 2026 and uses legacy device identifiers."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Automatic (legacy)"
              })
            }), _jsx(_components.td, {
              children: "Your sync predates this setting. Hightouch picks V2 for rows with an SDID value and V1 otherwise."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["V2 requires mapping the ", _jsx(_components.code, {
          children: "sdid"
        }), " field. Rows with an empty SDID value are rejected with an error instead of being sent to V1. New syncs default to V2."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Custom ID event syncs always use V1. Singular doesn't document a V2 equivalent\nfor custom ID events."
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "Platform"
        }), ",", _jsx(_components.code, {
          children: "Package Name"
        }), ", and ", _jsx(_components.code, {
          children: "OS Version"
        }), " fields are the minimum required fields to make the API call to Singular. For a richer analytics, we recommend that you provide all relevant fields in your use case.\n", _jsx(_components.img, {
          src: "destinations/destination-singular-dst-mappings.png",
          alt: ""
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The supported platforms are ", _jsx(_components.code, {
            children: "iOS"
          }), " and ", _jsx(_components.code, {
            children: "Android"
          }), ", case sensitive. If the\nplatform is ", _jsx(_components.code, {
            children: "Android"
          }), ", ", _jsx(_components.code, {
            children: "andi"
          }), " field is also required."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If you don't map the ", _jsx(_components.code, {
            children: "ip"
          }), " field, it will be defaulted with one from\nHightouch's servers."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can use custom mappings to attach custom data relevant to your application.\n", _jsx(_components.img, {
          src: "destinations/destination-singular-dst-custom-mappings.png",
          alt: ""
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Custom mappings are included as the ", _jsxs(_components.a, {
            href: "https://support.singular.net/hc/en-us/articles/31496864868635-Server-to-Server-EVENT-Endpoint-API-Reference",
            children: [_jsx(_components.code, {
              children: "e"
            }), "\nparameter"]
          }), "\nof the API request payload."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about required fields, refer to ", _jsx(_components.a, {
          href: "https://support.singular.net/hc/en-us/articles/31496864868635-Server-to-Server-EVENT-Endpoint-API-Reference",
          children: "Singular's documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs(_components.p, {
        children: ["If you receive errors, ensure that your including all of Singular's required fields: ", _jsx(_components.code, {
          children: "Platform"
        }), ", ", _jsx(_components.code, {
          children: "Package Name"
        }), ", ", _jsx(_components.code, {
          children: "OS Version"
        }), " and ", _jsx(_components.code, {
          children: "andi"
        }), " if your ", _jsx(_components.code, {
          children: "Platform"
        }), " is ", _jsx(_components.code, {
          children: "Android"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
